import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as ToastPrim from '@radix-ui/react-toast';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { PlanRequiredEventDetail } from '@/lib/api';

export type ToastVariant = 'success' | 'error' | 'neutral';

interface ToastItem {
  id: number;
  message: ReactNode;
  variant: ToastVariant;
}

interface ToastApi {
  success: (message: ReactNode) => void;
  error: (message: ReactNode) => void;
  neutral: (message: ReactNode) => void;
  show: (message: ReactNode, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const variantStyle: Record<ToastVariant, string> = {
  success:
    'border-emerald-200 bg-white text-ink-900 [&_[data-toast-marker]]:bg-emerald-500',
  error:
    'border-warn-500/30 bg-white text-warn-600 [&_[data-toast-marker]]:bg-warn-600',
  neutral:
    'border-ink-200 bg-white text-ink-700 [&_[data-toast-marker]]:bg-ink-700',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const counter = useRef(0);
  const { t } = useTranslation();

  const push = useCallback((message: ReactNode, variant: ToastVariant) => {
    counter.current += 1;
    const id = counter.current;
    setItems((prev) => [...prev, { id, message, variant }]);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push(m, 'success'),
      error: (m) => push(m, 'error'),
      neutral: (m) => push(m, 'neutral'),
      show: (m, v = 'neutral') => push(m, v),
    }),
    [push],
  );

  /* Listen for plan_required 403s dispatched from the axios interceptor.
   * The interceptor lives outside React so it can't call useToast() — this
   * is the bridge. Dedup by required-plan so a burst of requests doesn't
   * spam the user with identical toasts. */
  const lastShownAt = useRef<Record<string, number>>({});
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<PlanRequiredEventDetail>).detail;
      if (!detail) return;
      const now = Date.now();
      if ((lastShownAt.current[detail.required] ?? 0) > now - 3000) return;
      lastShownAt.current[detail.required] = now;
      const planLabel = t(`plan.tier_${detail.required}`);
      // Best-effort: try to keep us inside the same workspace, otherwise
      // fall back to relative anchor. We can't useParams() here.
      const m = typeof window !== 'undefined'
        ? window.location.pathname.match(/\/app\/workspace\/([^/]+)/)
        : null;
      const wsId = m?.[1];
      const target = wsId
        ? `/app/workspace/${wsId}/settings#billing`
        : '#billing';
      push(
        <span className="flex items-center gap-2 flex-wrap">
          <span>{t('plan.locked_toast', { plan: planLabel })}</span>
          <Link
            to={target}
            className="font-semibold text-accent-700 hover:text-accent-800 underline"
          >
            {t('plan.upgrade_cta', { plan: planLabel })}
          </Link>
        </span>,
        'error',
      );
    };
    window.addEventListener('patronez:plan-required', handler as EventListener);
    return () =>
      window.removeEventListener('patronez:plan-required', handler as EventListener);
  }, [push, t]);

  return (
    <ToastContext.Provider value={api}>
      <ToastPrim.Provider duration={4000} swipeDirection="right">
        {children}
        {items.map((it) => (
          <ToastPrim.Root
            key={it.id}
            onOpenChange={(open) => {
              if (!open) setItems((prev) => prev.filter((p) => p.id !== it.id));
            }}
            className={cn(
              'group flex items-center gap-3 rounded-xl border px-4 py-3 text-sm',
              'data-[state=open]:animate-in data-[state=open]:slide-in-from-right-2',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out',
              variantStyle[it.variant],
            )}
          >
            <span
              data-toast-marker
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
              aria-hidden
            />
            <ToastPrim.Description className="flex-1 leading-snug">
              {it.message}
            </ToastPrim.Description>
            <ToastPrim.Close
              className="shrink-0 rounded-md p-1 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5" />
            </ToastPrim.Close>
          </ToastPrim.Root>
        ))}
        <ToastPrim.Viewport className="fixed bottom-4 right-4 z-[60] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none" />
      </ToastPrim.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Safe fallback so accidental usage outside provider does not crash;
    // toasts only appear when the provider is mounted (in main.tsx).
    return {
      success: () => undefined,
      error: () => undefined,
      neutral: () => undefined,
      show: () => undefined,
    };
  }
  return ctx;
}
